import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { ReadOnlyBanner } from '@client/components/admin/ReadOnlyBanner';
import { CommunicationTemplateChannel } from '@client/components/communication-templates/CommunicationTemplateChannel';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, type PrismaTableColumn, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { downloadBlob } from '@client/utils/common/blob';
import { formatCommTemplateRelatedTo } from '@client/utils/formatters/comm-template';
import { COMM_TEMPLATE_STATUS_LABELS, type CommTemplateStatus, DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Space, Tag } from 'antd';
import type { Key } from 'antd/es/table/interface';
import { Download, Mail, Upload } from 'lucide-react';
import { useState } from 'react';
import { CommTemplateFilters } from './CommTemplateFilters';
import { ImportCommTemplatesModal } from './ImportCommTemplatesModal';

type QueryParams = RouterInputs['commTemplate']['listCommTemplates'];
type CommTemplateRow = NonNullable<RouterOutputs['commTemplate']['listCommTemplates']>['rows'][number];
type CommTemplateOrderBy = NonNullable<QueryParams['orderBy']>;

function useCommTemplateColumns(
    formatDateTime: (value: any) => string,
): readonly PrismaTableColumn<CommTemplateRow, CommTemplateOrderBy, any>[] {
    const columnHelper = createColumnHelper<CommTemplateRow, CommTemplateOrderBy>();

    return [
        columnHelper.accessor('name', {
            id: 'name',
            header: 'Name',
            sortable: 'name',
            cell: ({ row }) => row.name,
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('internalName', {
            id: 'internalName',
            header: 'Internal Name',
            sortable: 'internalName',
            cell: ({ row }) => row.internalName,
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('channel', {
            id: 'channel',
            header: 'Channel',
            sortable: 'channel',
            cell: ({ row }) => <CommunicationTemplateChannel channel={row.channel} />,
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('relatedTo', {
            id: 'relatedTo',
            header: 'Related To',
            sortable: 'relatedTo',
            cell: ({ row }) => formatCommTemplateRelatedTo(row.relatedTo),
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('status', {
            id: 'status',
            header: 'Status',
            sortable: 'status',
            cell: ({ row }) => {
                const statusColors: Record<CommTemplateStatus, string> = {
                    DRAFT: 'default',
                    PUBLISHED: 'success',
                    ARCHIVED: 'error',
                };
                return row.status ? (
                    <Tag color={statusColors[row.status] || 'default'}>
                        {COMM_TEMPLATE_STATUS_LABELS[row.status] || row.status}
                    </Tag>
                ) : null;
            },
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('version', {
            id: 'version',
            header: 'Version',
            sortable: 'version',
            cell: ({ row }) => (row.version ? `v${row.version}` : null),
            className: 'whitespace-nowrap',
        }),
        columnHelper.accessor('createdAt', {
            id: 'createdAt',
            header: 'Created at',
            sortable: 'createdAt',
            cell: ({ row }) => formatDateTime(row.createdAt),
            className: 'whitespace-nowrap',
        }),
    ] as const;
}

export const Route = createFileRoute('/admin-console/platform-content/comms-and-content/comm-templates')({
    staticData: {
        ui: {
            icon: Mail,
            title: 'Communication templates',
            widthVariant: 'fullWidth',
            menuDescription: 'Manage email templates',
        },
        breadcrumb: { title: 'Communication templates' },
    },
    component: CommTemplates,
});

function CommTemplates() {
    const { trpc, trpcClient } = useTrpc();
    const navigate = useNavigate();
    const { formatDateTime } = useFormatter();
    const { isReadOnly } = useAdminModulePermissions('comms');
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<bigint[]>([]);

    const [queryParams, setQueryParams] = usePathState<QueryParams>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
        orderBy: { createdAt: 'desc' },
        where: {},
    });

    const {
        data: commTemplates,
        isPending,
        error,
        refetch,
    } = trpc.commTemplate.listCommTemplates.useQuery(queryParams);

    const handleExport = async () => {
        try {
            setIsExporting(true);
            const result = await trpcClient.commTemplate.exportCommTemplates.query({
                ids: selectedRowKeys.length > 0 ? selectedRowKeys : undefined,
                where: queryParams.where,
            });
            const blob = new Blob([result.json], { type: 'application/json;charset=utf-8;' });
            downloadBlob(blob, result.filename);
            message.success(`Exported ${result.count} communication templates`);
        } catch (error) {
            errorMessage.show(error);
        } finally {
            setIsExporting(false);
        }
    };

    const columns = useCommTemplateColumns(formatDateTime);

    const tableProps = usePrismaTable({
        data: commTemplates?.rows || [],
        total: commTemplates?.total || 0,
        loading: isPending,
        columns,
        rowKey: 'id',
        queryParams: queryParams,
        onParamsChange: setQueryParams,
        rowSelection: {
            enabled: true,
            type: 'checkbox',
            preserveSelectedRowKeys: true,
            selectedRowKeys: selectedRowKeys as unknown as Key[],
            onChange: (keys: Key[]) => {
                setSelectedRowKeys(keys.map((k) => BigInt(String(k))));
            },
        },
    });

    return (
        <PageController route={Route}>
            <TRPCErrorBoundary error={error}>
                <ReadOnlyBanner module="comms" />
                <TableToolbar
                    filters={
                        <CommTemplateFilters onChange={(where) => setQueryParams((prev) => ({ ...prev, where }))} />
                    }
                    actions={
                        <Space>
                            <Button type="secondary" icon={<Upload />} onClick={handleExport} loading={isExporting}>
                                {selectedRowKeys.length > 0
                                    ? `Export selected (${selectedRowKeys.length})`
                                    : 'Export all'}
                            </Button>
                            {!isReadOnly && (
                                <>
                                    <Button
                                        type="secondary"
                                        icon={<Download />}
                                        onClick={() => setIsImportModalOpen(true)}
                                    >
                                        Import
                                    </Button>
                                    <Button.Add.Link
                                        to="/admin-console/platform-content/comms-and-content/comm-templates/add"
                                        variant="primary"
                                        label="New template"
                                    />
                                </>
                            )}
                        </Space>
                    }
                />

                <PrismaTable
                    {...tableProps}
                    scroll={{ x: 'max-content' }}
                    onRow={(record) => ({
                        className: 'cursor-pointer',
                        onClick(e) {
                            navigate(
                                {
                                    to: '/admin-console/platform-content/comms-and-content/comm-templates/edit/$commTemplateId',
                                    params: { commTemplateId: record.id },
                                },
                                e,
                            );
                        },
                    })}
                />
                <Outlet />

                <ImportCommTemplatesModal
                    open={isImportModalOpen}
                    onClose={() => setIsImportModalOpen(false)}
                    onSuccess={() => void refetch()}
                />
            </TRPCErrorBoundary>
        </PageController>
    );
}
